import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PlusIcon, SearchIcon, SparklesIcon, TrendingUpIcon, XIcon, FilterIcon } from 'lucide-react';
import { mockPlaybooksNew } from '../mockData';
import { GatedContentForm } from '../components/auth/GatedContentForm';
export function PlaybooksPage() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedLifecycle, setSelectedLifecycle] = useState<string[]>([]);
  const [selectedMotion, setSelectedMotion] = useState<string[]>([]);
  const [showFilters, setShowFilters] = useState(false);
  const [isGatedFormOpen, setIsGatedFormOpen] = useState(false);
  const [selectedPlaybook, setSelectedPlaybook] = useState<any>(null);
  const lifecycleOptions = ['Acquire', 'Close', 'Expand'];
  const motionOptions = ['ABM', 'Inbound', 'Outbound', 'Event', 'PLG', 'CS'];
  const getLifecycleColor = (lifecycle: string) => {
    switch (lifecycle) {
      case 'Acquire':
        return 'bg-green-500';
      case 'Close':
        return 'bg-blue-500';
      case 'Expand':
        return 'bg-purple-500';
      default:
        return 'bg-gray-500';
    }
  };
  const getComplexityBadge = (complexity: string) => {
    const badges = {
      simple: {
        label: 'Starter',
        color: 'bg-green-100 text-green-700'
      },
      standard: {
        label: 'Standard',
        color: 'bg-blue-100 text-blue-700'
      },
      advanced: {
        label: 'Advanced',
        color: 'bg-orange-100 text-orange-700'
      }
    };
    return badges[complexity as keyof typeof badges] || badges.standard;
  };
  const filteredPlaybooks = mockPlaybooksNew.filter(playbook => {
    const matchesSearch = playbook.title.toLowerCase().includes(searchQuery.toLowerCase()) || playbook.summary.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesLifecycle = selectedLifecycle.length === 0 || playbook.lifecycle.some(l => selectedLifecycle.includes(l));
    const matchesMotion = selectedMotion.length === 0 || playbook.motion.some(m => selectedMotion.includes(m));
    return matchesSearch && matchesLifecycle && matchesMotion;
  });
  const toggleFilter = (value: string, selected: string[], setSelected: (values: string[]) => void) => {
    if (selected.includes(value)) {
      setSelected(selected.filter(v => v !== value));
    } else {
      setSelected([...selected, value]);
    }
  };
  const clearAllFilters = () => {
    setSelectedLifecycle([]);
    setSelectedMotion([]);
  };
  const hasActiveFilters = selectedLifecycle.length > 0 || selectedMotion.length > 0;
  const handlePlaybookClick = (playbook: any) => {
    // Check if user is logged in (you can add actual auth check here)
    const isLoggedIn = true; // Replace with actual auth check
    if (isLoggedIn) {
      navigate(`/playbooks/${playbook.id}`);
    } else {
      setSelectedPlaybook(playbook);
      setIsGatedFormOpen(true);
    }
  };
  const handleGatedFormSubmit = (data: any) => {
    console.log('Gated form submitted:', data);
    // After successful submission, navigate to playbook detail
    if (selectedPlaybook) {
      navigate(`/playbooks/${selectedPlaybook.id}`);
    }
  };
  return <div className="w-full min-h-screen bg-gradient-to-br from-gray-50 to-gray-100" data-id="element-5714">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8" data-id="element-5715">
        {/* Hero */}
        <div className="mb-8" data-id="element-5716">
          <div className="flex items-start justify-between mb-4" data-id="element-5717">
            <div data-id="element-5718">
              <h1 className="text-4xl font-bold text-gray-900 mb-2" data-id="element-5719">
                Lifecycle Playbooks
              </h1>
              <p className="text-lg text-gray-600" data-id="element-5720">
                Signal-based GTM programs that move accounts from intent to
                revenue
              </p>
            </div>
            <button onClick={() => navigate('/playbooks/create')} className="px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-all shadow-lg hover:shadow-xl font-medium flex items-center gap-2" data-id="element-5721">
              <PlusIcon className="w-5 h-5" data-id="element-5722" />
              Create Playbook
            </button>
          </div>
          <div className="flex items-center gap-4" data-id="element-5723">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-50 border border-blue-200 rounded-lg" data-id="element-5724">
              <SparklesIcon className="w-5 h-5 text-blue-600" data-id="element-5725" />
              <span className="text-sm text-blue-900 font-medium" data-id="element-5726">
                AI-Ready plays run on Copilot Agents
              </span>
            </div>
            <button onClick={() => navigate('/plays/library')} className="text-sm text-gray-600 hover:text-gray-900 font-medium" data-id="element-5727">
              Browse Play Library →
            </button>
          </div>
        </div>
        {/* Compact Filters */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4 mb-6" data-id="element-5728">
          <div className="flex items-center gap-4" data-id="element-5729">
            {/* Search */}
            <div className="relative flex-1" data-id="element-5730">
              <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" data-id="element-5731" />
              <input type="text" placeholder="Search playbooks..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent" data-id="element-5732" />
            </div>
            {/* Filter Toggle */}
            <button onClick={() => setShowFilters(!showFilters)} className={`px-4 py-2 rounded-lg font-medium flex items-center gap-2 transition-colors ${showFilters || hasActiveFilters ? 'bg-red-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} data-id="element-5733">
              <FilterIcon className="w-4 h-4" data-id="element-5734" />
              Filters
              {hasActiveFilters && <span className="px-2 py-0.5 bg-white text-red-600 text-xs rounded-full font-bold" data-id="element-5735">
                  {selectedLifecycle.length + selectedMotion.length}
                </span>}
            </button>
            {hasActiveFilters && <button onClick={clearAllFilters} className="text-sm text-red-600 hover:text-red-700 font-medium flex items-center gap-1" data-id="element-5736">
                <XIcon className="w-4 h-4" data-id="element-5737" />
                Clear
              </button>}
          </div>
          {/* Expandable Filters */}
          {showFilters && <div className="mt-4 pt-4 border-t border-gray-200 space-y-3" data-id="element-5738">
              <div data-id="element-5739">
                <label className="text-xs font-semibold text-gray-600 uppercase mb-2 block" data-id="element-5740">
                  Lifecycle
                </label>
                <div className="flex flex-wrap gap-2" data-id="element-5741">
                  {lifecycleOptions.map(option => <button key={option} onClick={() => toggleFilter(option, selectedLifecycle, setSelectedLifecycle)} className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${selectedLifecycle.includes(option) ? 'bg-red-600 text-white shadow-md' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} data-id="element-5742">
                      {option}
                    </button>)}
                </div>
              </div>
              <div data-id="element-5743">
                <label className="text-xs font-semibold text-gray-600 uppercase mb-2 block" data-id="element-5744">
                  Motion
                </label>
                <div className="flex flex-wrap gap-2" data-id="element-5745">
                  {motionOptions.map(option => <button key={option} onClick={() => toggleFilter(option, selectedMotion, setSelectedMotion)} className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${selectedMotion.includes(option) ? 'bg-red-600 text-white shadow-md' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} data-id="element-5746">
                      {option}
                    </button>)}
                </div>
              </div>
            </div>}
        </div>
        {/* Playbooks Grid */}
        <div className="mb-6" data-id="element-5747">
          <div className="flex items-center justify-between mb-4" data-id="element-5748">
            <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2" data-id="element-5749">
              <TrendingUpIcon className="w-5 h-5 text-red-600" data-id="element-5750" />
              All Playbooks ({filteredPlaybooks.length})
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-5751">
            {filteredPlaybooks.map(playbook => {
            const complexityBadge = getComplexityBadge(playbook.complexity);
            return <div key={playbook.id} onClick={() => handlePlaybookClick(playbook)} className="bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-2xl hover:border-red-300 transition-all cursor-pointer group transform hover:-translate-y-1" data-id="element-5752">
                  {/* Color Strip */}
                  <div className={`h-3 ${getLifecycleColor(playbook.lifecycle[0])}`} data-id="element-5753" />
                  {/* Content */}
                  <div className="p-6" data-id="element-5754">
                    {/* Header with Icon */}
                    <div className="flex items-start gap-4 mb-4" data-id="element-5755">
                      <div className="text-5xl" data-id="element-5756">{playbook.icon}</div>
                      <div className="flex-1" data-id="element-5757">
                        <h3 className="text-lg font-bold text-gray-900 group-hover:text-red-600 transition-colors mb-1" data-id="element-5758">
                          {playbook.title}
                        </h3>
                        <div className="flex items-center gap-2" data-id="element-5759">
                          {playbook.ai_ready && <div className="flex items-center gap-1 px-2 py-0.5 bg-purple-100 text-purple-700 rounded text-xs font-medium" data-id="element-5760">
                              <SparklesIcon className="w-3 h-3" data-id="element-5761" />
                              AI-Ready
                            </div>}
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${complexityBadge.color}`} data-id="element-5762">
                            {complexityBadge.label}
                          </span>
                        </div>
                      </div>
                    </div>
                    <p className="text-sm text-gray-600 mb-4 line-clamp-3" data-id="element-5763">
                      {playbook.summary}
                    </p>
                    {/* Tags */}
                    <div className="flex flex-wrap gap-2 mb-4" data-id="element-5764">
                      {playbook.lifecycle.map(lifecycle => <span key={lifecycle} className="px-3 py-1 bg-gray-100 text-gray-700 text-xs font-medium rounded-full" data-id="element-5765">
                          {lifecycle}
                        </span>)}
                      {playbook.motion.slice(0, 2).map(motion => <span key={motion} className="px-3 py-1 bg-blue-100 text-blue-700 text-xs font-medium rounded-full" data-id="element-5766">
                          {motion}
                        </span>)}
                    </div>
                    {/* Metrics */}
                    <div className="grid grid-cols-2 gap-3 mb-4" data-id="element-5767">
                      <div className="p-3 bg-gradient-to-br from-green-50 to-green-100 rounded-lg border border-green-200" data-id="element-5768">
                        <p className="text-xs text-green-700 mb-1 font-medium" data-id="element-5769">
                          Expected Lift
                        </p>
                        <p className="text-lg font-bold text-green-900" data-id="element-5770">
                          {playbook.expected_lift}
                        </p>
                      </div>
                      <div className="p-3 bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg border border-blue-200" data-id="element-5771">
                        <p className="text-xs text-blue-700 mb-1 font-medium" data-id="element-5772">
                          Time to Value
                        </p>
                        <p className="text-lg font-bold text-blue-900" data-id="element-5773">
                          {playbook.time_to_value}
                        </p>
                      </div>
                    </div>
                    {/* Progress Bar */}
                    {playbook.activation_rate !== undefined && <div data-id="element-5774">
                        <div className="flex items-center justify-between mb-2" data-id="element-5775">
                          <p className="text-xs text-gray-600 font-medium" data-id="element-5776">
                            Activation (30d)
                          </p>
                          <p className="text-xs font-bold text-gray-900" data-id="element-5777">
                            {playbook.activation_rate}%
                          </p>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2.5" data-id="element-5778">
                          <div className="bg-gradient-to-r from-red-500 to-red-600 h-2.5 rounded-full transition-all shadow-sm" style={{
                      width: `${playbook.activation_rate}%`
                    }} data-id="element-5779" />
                        </div>
                      </div>}
                  </div>
                </div>;
          })}
          </div>
        </div>
        {/* Empty State */}
        {filteredPlaybooks.length === 0 && <div className="text-center py-16 bg-white rounded-lg border border-gray-200" data-id="element-5780">
            <div className="text-6xl mb-4" data-id="element-5781">🔍</div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2" data-id="element-5782">
              No playbooks found
            </h3>
            <p className="text-gray-600 mb-6" data-id="element-5783">
              Try adjusting your filters or search query
            </p>
            <button onClick={clearAllFilters} className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium" data-id="element-5784">
              Clear all filters
            </button>
          </div>}
      </div>
      {/* Gated Content Modal */}
      <GatedContentForm isOpen={isGatedFormOpen} onClose={() => setIsGatedFormOpen(false)} contentTitle={selectedPlaybook?.title} contentType="playbook" onSubmit={handleGatedFormSubmit} data-id="element-5785" />
    </div>;
}